import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from '../i18n/useTranslation';

const backendUrl = 'http://localhost:3001/api';

interface Disbursement {
  applicationId: string;
  applicantName: string;
  applicantEmail: string;
  merchantId: string;
  merchantName: string;
  requestedAmount: number;
  disbursementDate: string;
  creditScore: number;
}

interface Repayment {
  repaymentId: string;
  applicationId: string;
  applicantName: string;
  applicantEmail: string;
  merchantId: string;
  merchantName: string;
  repaymentAmount: number;
  paymentDate: string | null;
  dueDate: string;
  actualStatus: string;
  requestedAmount: number;
  creditScore: number;
}

const TransactionDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [disbursements, setDisbursements] = useState<Disbursement[]>([]);
  const [repayments, setRepayments] = useState<Repayment[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [disbursementPagination, setDisbursementPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  const [repaymentPagination, setRepaymentPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  // Filters
  const [filters, setFilters] = useState({
    merchantId: '',
    start: '',
    end: '',
    status: '',
    page: 1,
    limit: 10
  });

  const fetchDisbursements = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/transactions/disbursements`, { params: filters });
      if (response.data.data) {
        setDisbursements(response.data.data);
        setDisbursementPagination(response.data.pagination);
      } else {
        setDisbursements(response.data);
        setDisbursementPagination({
          currentPage: 1,
          totalPages: 1,
          totalRecords: response.data.length,
          recordsPerPage: filters.limit,
          hasNextPage: false,
          hasPreviousPage: false
        });
      }
    } catch (error) {
      console.error('Error fetching disbursements:', error);
    }
    setLoading(false);
  };

  const fetchRepayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/transactions/repayments`, { params: filters });
      if (response.data.data) {
        setRepayments(response.data.data);
        setRepaymentPagination(response.data.pagination);
      } else {
        setRepayments(response.data);
        setRepaymentPagination({
          currentPage: 1,
          totalPages: 1,
          totalRecords: response.data.length,
          recordsPerPage: filters.limit,
          hasNextPage: false,
          hasPreviousPage: false
        });
      }
    } catch (error) {
      console.error('Error fetching repayments:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchDisbursements();
    } else {
      fetchRepayments();
    }
  }, [activeTab, filters.page, filters.limit, filters.merchantId, filters.start, filters.end, filters.status]);

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 })); // Reset to page 1 when filters change
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 })); // Reset to page 1 when changing limit
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const exportData = () => {
    const data = activeTab === 0 ? disbursements : repayments;
    const headers = activeTab === 0 
      ? ['Loan ID', 'Applicant', 'Merchant', 'Amount (ZiG)', 'Disbursement Date', 'Credit Score']
      : ['Repayment ID', 'Loan ID', 'Applicant', 'Merchant', 'Amount (ZiG)', 'Due Date', 'Payment Date', 'Status'];
    
    const csvContent = [
      headers.join(','),
      ...data.map(item => 
        activeTab === 0 
          ? [
              (item as Disbursement).applicationId,
              (item as Disbursement).applicantName,
              (item as Disbursement).merchantName,
              (item as Disbursement).requestedAmount,
              (item as Disbursement).disbursementDate,
              (item as Disbursement).creditScore
            ].join(',')
          : [
              (item as Repayment).repaymentId,
              (item as Repayment).applicationId,
              (item as Repayment).applicantName,
              (item as Repayment).merchantName,
              (item as Repayment).repaymentAmount,
              (item as Repayment).dueDate,
              (item as Repayment).paymentDate || 'N/A',
              (item as Repayment).actualStatus
            ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab === 0 ? 'disbursements' : 'repayments'}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>
          Transaction Details
        </h1>
        <p style={{ color: '#64748b', fontSize: 16 }}>
          Comprehensive view of loan disbursements and repayment transactions
        </p>
      </div>

      {/* Filters */}
      <Card elevation={2} sx={{ marginBottom: 3 }}>
        <CardHeader 
          title={t('transactionDetails.filters')} 
          sx={{ 
            bgcolor: '#1565c0',
            color: 'white',
            '& .MuiCardHeader-title': { fontWeight: 600 }
          }}
        />
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'end' }}>
            <TextField
              label={t('transactionDetails.merchantId')}
              value={filters.merchantId}
              onChange={(e) => handleFilterChange('merchantId', e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
            />
            <TextField
              label={t('transactionDetails.startDate')}
              type="date"
              value={filters.start}
              onChange={(e) => handleFilterChange('start', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t('transactionDetails.endDate')}
              type="date"
              value={filters.end}
              onChange={(e) => handleFilterChange('end', e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            {activeTab === 1 && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              label="Limit"
              type="number"
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', Number(e.target.value))}
              size="small"
              sx={{ width: 100 }}
            />
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportData}
              sx={{ 
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Export CSV
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab 
              label={`Disbursements (${disbursements.length})`} 
              sx={{ fontWeight: 600 }}
            />
            <Tab 
              label={`Repayments (${repayments.length})`} 
              sx={{ fontWeight: 600 }}
            />
          </Tabs>
        </Box>

        <CardContent sx={{ padding: 0 }}>
          {/* Disbursements Tab */}
          {activeTab === 0 && (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Loan ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Applicant</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Merchant</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Amount (ZiG)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Disbursement Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Credit Score</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {disbursements.map((disbursement) => (
                      <TableRow key={disbursement.applicationId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {disbursement.applicationId.slice(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Typography variant="body2" fontWeight={500}>
                              {disbursement.applicantName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {disbursement.applicantEmail}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Typography variant="body2" fontWeight={500}>
                              {disbursement.merchantName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {disbursement.merchantId}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="#1565c0">
                            {disbursement.requestedAmount.toLocaleString('en-ZW')} ZiG
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {disbursement.disbursementDate 
                              ? new Date(disbursement.disbursementDate).toLocaleDateString('en-ZW')
                              : 'Not disbursed'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={disbursement.creditScore} 
                            color={disbursement.creditScore >= 700 ? 'success' : disbursement.creditScore >= 600 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Loan Details">
                            <IconButton 
                              size="small" 
                              onClick={() => navigate(`/application/${disbursement.applicationId}`)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination Controls for Disbursements */}
              {disbursementPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {((disbursementPagination.currentPage - 1) * disbursementPagination.recordsPerPage) + 1} to {Math.min(disbursementPagination.currentPage * disbursementPagination.recordsPerPage, disbursementPagination.totalRecords)} of {disbursementPagination.totalRecords} records
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={filters.limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!disbursementPagination.hasPreviousPage}
                      onClick={() => handlePageChange(disbursementPagination.currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
                      Page {disbursementPagination.currentPage} of {disbursementPagination.totalPages}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!disbursementPagination.hasNextPage}
                      onClick={() => handlePageChange(disbursementPagination.currentPage + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Repayments Tab */}
          {activeTab === 1 && (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Repayment ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Loan ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Applicant</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Merchant</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Amount (ZiG)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Payment Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {repayments.map((repayment) => (
                      <TableRow key={repayment.repaymentId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {repayment.repaymentId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {repayment.applicationId.slice(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Typography variant="body2" fontWeight={500}>
                              {repayment.applicantName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {repayment.applicantEmail}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Typography variant="body2" fontWeight={500}>
                              {repayment.merchantName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {repayment.merchantId}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="#12b569">
                            {repayment.repaymentAmount.toLocaleString('en-ZW')} ZiG
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(repayment.dueDate).toLocaleDateString('en-ZW')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {repayment.paymentDate 
                              ? new Date(repayment.paymentDate).toLocaleDateString('en-ZW')
                              : 'Not paid'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={repayment.actualStatus}
                            color={getStatusColor(repayment.actualStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Loan Details">
                            <IconButton 
                              size="small" 
                              onClick={() => navigate(`/application/${repayment.applicationId}`)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination Controls for Repayments */}
              {repaymentPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {((repaymentPagination.currentPage - 1) * repaymentPagination.recordsPerPage) + 1} to {Math.min(repaymentPagination.currentPage * repaymentPagination.recordsPerPage, repaymentPagination.totalRecords)} of {repaymentPagination.totalRecords} records
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={filters.limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!repaymentPagination.hasPreviousPage}
                      onClick={() => handlePageChange(repaymentPagination.currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
                      Page {repaymentPagination.currentPage} of {repaymentPagination.totalPages}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!repaymentPagination.hasNextPage}
                      onClick={() => handlePageChange(repaymentPagination.currentPage + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}

          {loading && (
            <Box sx={{ textAlign: 'center', padding: 4 }}>
              <Typography>Loading transactions...</Typography>
            </Box>
          )}

          {!loading && activeTab === 0 && disbursements.length === 0 && (
            <Box sx={{ textAlign: 'center', padding: 4 }}>
              <Typography color="text.secondary">No disbursements found</Typography>
            </Box>
          )}

          {!loading && activeTab === 1 && repayments.length === 0 && (
            <Box sx={{ textAlign: 'center', padding: 4 }}>
              <Typography color="text.secondary">No repayments found</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionDetails;
